/**
 * AI study analysis + radiologist review (RISPACS-1036 / 1088).
 * UI: radiologist selects Normal | Abnormal | Uncertain + optional Comment.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiPost, extractApiErrorMessage } from '../utils/apiClient';

export type AiReviewAction = 'ACCEPTED' | 'IGNORED' | 'OVERRIDDEN' | 'FEEDBACK';
export type AiClassification = 'NORMAL' | 'ABNORMAL';
export type AiRadiologistClassification = 'NORMAL' | 'ABNORMAL' | 'UNCERTAIN';

/** Radiologist classification dropdown options. */
export const AI_REVIEW_REMARKS = ['NORMAL', 'ABNORMAL', 'UNCERTAIN'] as const;

export type AiReviewRemark = (typeof AI_REVIEW_REMARKS)[number];

export const AI_REVIEW_REMARK_LABELS: Record<AiReviewRemark, string> = {
  NORMAL: 'Normal',
  ABNORMAL: 'Abnormal',
  UNCERTAIN: 'Uncertain',
};

export interface AiStudyAnalysis {
  id: number;
  study_instance_uid: string;
  facility_id?: number;
  status: string;
  classification?: AiClassification | null;
  model_name?: string | null;
  model_version?: string | null;
  completed_at?: string | null;
  started_at?: string | null;
  review_action?: AiReviewAction | null;
  override_classification?: AiRadiologistClassification | null;
  feedback_type?: string | null;
  review_comment?: string | null;
  reviewed_by_user_id?: number | null;
  reviewed_at?: string | null;
  draft_id?: string | null;
  draft_report?: string | null;
  report_model_version?: string | null;
  remote_study_id?: string | null;
}

export interface AiReviewSubmitBody {
  action: AiReviewAction;
  override_classification?: AiRadiologistClassification | null;
  /** Alias accepted by backend */
  classification?: AiRadiologistClassification | null;
  feedback_type?: string | null;
  comment?: string | null;
}

function flipBinaryClassification(value: string | null | undefined): AiReviewRemark | '' {
  const normalized = String(value || '').toUpperCase();
  if (normalized === 'NORMAL') {
    return 'ABNORMAL';
  }
  if (normalized === 'ABNORMAL') {
    return 'NORMAL';
  }
  return '';
}

/** True when radiologist classification is already stored (RISPACS-1088 approval gate). */
export function hasPersistedRadiologistFeedback(analysis: AiStudyAnalysis | null): boolean {
  if (!analysis?.reviewed_at) {
    return false;
  }
  const action = String(analysis.review_action || '').toUpperCase();
  if (action === 'OVERRIDDEN') {
    return Boolean(analysis.override_classification);
  }
  if (action !== 'FEEDBACK') {
    return false;
  }

  const selected = String(analysis.override_classification || '').toUpperCase();
  if (selected === 'NORMAL' || selected === 'ABNORMAL' || selected === 'UNCERTAIN') {
    return true;
  }

  const type = String(analysis.feedback_type || '').toUpperCase();
  return type === 'CORRECT' || type === 'INCORRECT';
}

/** Comment is optional for all classification choices. */
export function remarkRequiresComment(_remark: AiReviewRemark | ''): boolean {
  return false;
}

export function remarkToReviewBody(remark: AiReviewRemark, comment: string): AiReviewSubmitBody {
  const trimmed = comment.trim();
  return {
    action: 'FEEDBACK',
    override_classification: remark,
    classification: remark,
    comment: trimmed || null,
  };
}

export function analysisToRemark(analysis: AiStudyAnalysis | null): AiReviewRemark | '' {
  if (analysis?.review_action !== 'FEEDBACK') {
    return '';
  }

  const selected = String(analysis.override_classification || '').toUpperCase();
  if (selected === 'NORMAL' || selected === 'ABNORMAL' || selected === 'UNCERTAIN') {
    return selected;
  }

  // Legacy Correct / Incorrect → show equivalent classification in dropdown
  const type = String(analysis.feedback_type || '').toUpperCase();
  if (type === 'CORRECT') {
    const ai = String(analysis.classification || '').toUpperCase();
    if (ai === 'NORMAL' || ai === 'ABNORMAL') {
      return ai;
    }
  }
  if (type === 'INCORRECT') {
    return flipBinaryClassification(analysis.classification);
  }
  return '';
}

/** True when remark/comment match what is already persisted on the analysis row. */
export function isAiReviewUnchanged(
  analysis: AiStudyAnalysis,
  remark: AiReviewRemark | '',
  comment: string
): boolean {
  const persistedRemark = analysisToRemark(analysis);
  const persistedComment = String(analysis.review_comment || '').trim();
  const currentComment = String(comment || '').trim();
  if (!remark && !persistedRemark) {
    return true;
  }
  if (!remark && persistedRemark) {
    return true;
  }
  return remark === persistedRemark && currentComment === persistedComment;
}

/** True when final approver must re-save feedback (e.g. junior saved, senior approves). */
export function needsApproverReviewReconfirmation(
  analysis: AiStudyAnalysis | null,
  approverUserId: number | string | null | undefined
): boolean {
  if (!analysis?.reviewed_at || !hasPersistedRadiologistFeedback(analysis)) {
    return false;
  }
  const approverId = Number(approverUserId);
  const reviewerId = Number(analysis.reviewed_by_user_id);
  if (!Number.isFinite(approverId) || approverId <= 0) {
    return false;
  }
  if (!Number.isFinite(reviewerId) || reviewerId <= 0) {
    return true;
  }
  return reviewerId !== approverId;
}

/** Draft explicitly applied to the current report editor (not latest study-level draft). */
export type ReportRadioAiDraftAssociation = {
  draftId: string;
  remoteStudyId: string;
};

/**
 * Build report save fields for RadioAI draft provenance.
 * Manual / non-Prashant reports must not send or must clear draft association.
 */
export function buildReportRadioAiDraftFields(params: {
  reportMode: number;
  usedPrashantDraftAssociation: ReportRadioAiDraftAssociation | null;
  isUpdateMode: boolean;
}): { radioai_draft_id?: string | null; radioai_remote_study_id?: string | null } {
  const { reportMode, usedPrashantDraftAssociation, isUpdateMode } = params;
  const draftId = String(usedPrashantDraftAssociation?.draftId || '').trim();
  const remoteStudyId = String(usedPrashantDraftAssociation?.remoteStudyId || '').trim();

  if (reportMode !== 1 || !draftId) {
    if (isUpdateMode) {
      return { radioai_draft_id: null, radioai_remote_study_id: null };
    }
    return {};
  }

  return {
    radioai_draft_id: draftId,
    radioai_remote_study_id: remoteStudyId || null,
  };
}

export type ApprovalAiReviewResolution =
  | { action: 'block'; reason: 'missing_remark' | 'reconfirmation_required' }
  | { action: 'skip' }
  | { action: 'submit'; remark: AiReviewRemark; comment: string };

/**
 * Decide whether to block, skip, or submit AI review before report approval.
 */
export function resolveApprovalAiReviewAction(params: {
  analysis: AiStudyAnalysis;
  aiReviewRemark: AiReviewRemark | '';
  aiReviewComment: string;
  approverUserId: number | string | null | undefined;
}): ApprovalAiReviewResolution {
  const { analysis, aiReviewRemark, aiReviewComment, approverUserId } = params;
  const persistedRemark = analysisToRemark(analysis);
  const alreadyPersisted = hasPersistedRadiologistFeedback(analysis);
  const mustReconfirm = needsApproverReviewReconfirmation(analysis, approverUserId);

  let effectiveRemark: AiReviewRemark | '' = aiReviewRemark;
  let effectiveComment = aiReviewComment;

  if (!effectiveRemark && persistedRemark && alreadyPersisted && !mustReconfirm) {
    effectiveRemark = persistedRemark;
    effectiveComment = analysis.review_comment || '';
  }

  if (mustReconfirm && !aiReviewRemark) {
    return { action: 'block', reason: 'reconfirmation_required' };
  }

  if (!effectiveRemark && !alreadyPersisted) {
    return { action: 'block', reason: 'missing_remark' };
  }

  if (
    effectiveRemark &&
    (mustReconfirm || !isAiReviewUnchanged(analysis, effectiveRemark, effectiveComment))
  ) {
    return {
      action: 'submit',
      remark: effectiveRemark,
      comment: effectiveComment,
    };
  }

  return { action: 'skip' };
}

export interface AiDraftReportResponse {
  ai_study_analysis_id: number;
  study_instance_uid: string;
  remote_study_id: string;
  classification?: AiClassification | null;
  draft_id: string;
  draft_report: string;
  report_model_version?: string | null;
  disclaimer?: string | null;
}

/** Use Prashant v5 draft report when chest XR worker produced NORMAL/ABNORMAL. */
export function shouldUsePrashantDraftReport(analysis: AiStudyAnalysis | null): boolean {
  if (!analysis) {
    return false;
  }
  const status = String(analysis.status || '').toUpperCase();
  if (status !== 'COMPLETED') {
    return false;
  }
  const classification = String(analysis.classification || '').toUpperCase();
  return classification === 'NORMAL' || classification === 'ABNORMAL';
}

export async function generatePrashantDraftReport(
  studyInstanceUid: string,
  findingsText: string
): Promise<AiDraftReportResponse> {
  const url = API_ENDPOINTS.AI_STUDY_ANALYSES.GENERATE_DRAFT_REPORT;
  try {
    const response = await apiPost<{
      success?: boolean;
      message?: string;
      data?: AiDraftReportResponse;
    }>(url, {
      study_instance_uid: studyInstanceUid,
      findings_text: findingsText,
    });
    if (!response.data?.success || !response.data.data?.draft_report) {
      throw new Error(response.data?.message || 'AI draft report generation failed');
    }
    return response.data.data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Failed to generate AI draft report'));
  }
}

type AiAnalysisApiBody = {
  success?: boolean;
  message?: string;
  data?: AiStudyAnalysis | null;
};

export async function fetchAiAnalysisByStudyUid(
  studyInstanceUid: string
): Promise<AiStudyAnalysis | null> {
  const url = API_ENDPOINTS.AI_STUDY_ANALYSES.BY_STUDY(studyInstanceUid);
  const response = await apiGet<AiAnalysisApiBody>(url);
  return response.data?.data ?? null;
}

export async function submitAiReview(
  analysisId: number,
  body: AiReviewSubmitBody
): Promise<AiStudyAnalysis> {
  const url = API_ENDPOINTS.AI_STUDY_ANALYSES.SUBMIT_REVIEW(analysisId);
  try {
    const response = await apiPost<AiAnalysisApiBody>(url, body);
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.message || 'AI review save failed');
    }
    return response.data.data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Failed to save AI review'));
  }
}
